import { useEffect, useCallback } from 'react';

interface UseSmoothScrollProps {
  headerSelector?: string;
  offset?: number;
  behavior?: ScrollBehavior;
}

const useSmoothScroll = ({
  headerSelector = 'header',
  offset = 20,
  behavior = 'smooth'
}: UseSmoothScrollProps = {}) => {

  const scrollToElement = useCallback((targetId: string) => {
    const targetElement = document.querySelector(targetId);
    
    if (targetElement) {
      const header = document.querySelector(headerSelector);
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = targetPosition - headerHeight - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: behavior
      });
      
      return true;
    }
    
    return false;
  }, [headerSelector, offset, behavior]);

  const handleClick = useCallback((event: Event) => {
    const target = event.target as HTMLAnchorElement;
    
    // Nur interne Links mit # verarbeiten
    if (target.tagName === 'A' && target.href.includes('#')) {
      // Ignoriere Links mit data-action Attribut
      if (target.hasAttribute('data-action')) {
        return;
      }
      
      const href = target.getAttribute('href');
      
      if (href && href.startsWith('#') && href !== '#') {
        event.preventDefault();
        
        const success = scrollToElement(href);
        
        if (success) {
          // Schließe mobiles Menü falls geöffnet
          const menuContainer = document.getElementById('menu-container');
          const menuButton = document.getElementById('menu-button');
          const closeButton = document.getElementById('close-button');
          
          if (menuContainer && menuContainer.style.display === 'flex') {
            menuContainer.style.display = 'none';
            if (menuButton && closeButton) {
              menuButton.classList.remove('hidden');
              closeButton.classList.add('hidden');
            }
          }
        }
      }
    }
  }, [scrollToElement]);

  useEffect(() => {
    // Event Delegation: Ein einziger Event Listener für das gesamte Dokument
    document.addEventListener('click', handleClick);
    
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [handleClick]);

  // Programmatische Scroll-Funktion für externe Nutzung
  const scrollTo = useCallback((targetId: string) => {
    return scrollToElement(targetId);
  }, [scrollToElement]);

  return { scrollTo };
};

export default useSmoothScroll;
